import MaterialIcon from '@components/common/MaterialIcon';
import { LANGUAGE_FLAG_EMOJIS } from '@conversed/shared/src/central/constants/languages';
import { purify } from '@helpers/domHelper';
import parse from 'html-react-parser';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import type { TTranslationState } from './conversation-review-modal.type';

type Props = {
  displayedAnswer: string;
  translationState: TTranslationState | null;
  isShowingOriginalLanguage: boolean;
};

const getLanguageNote = (
  translationState: TTranslationState | null,
  isShowingOriginalLanguage: boolean,
  t: (key: string, options?: Record<string, string>) => string,
): string | null => {
  if (!translationState) {
    return null;
  }
  if (isShowingOriginalLanguage) {
    const sourceFlag =
      LANGUAGE_FLAG_EMOJIS[translationState.sourceLang] ?? '🌐';
    return `${sourceFlag} ${t(
      'train-ai.conversation-review.language.responding-in-original',
      { language: translationState.sourceLanguageName },
    )}`;
  }
  return `${LANGUAGE_FLAG_EMOJIS.nl ?? '🇳🇱'} ${t(
    'train-ai.conversation-review.language.responding-in-dutch',
  )}`;
};

export const ConversationReviewAnswerSection: React.FC<Props> = ({
  displayedAnswer,
  translationState,
  isShowingOriginalLanguage,
}) => {
  const { t } = useTranslation();

  const languageNote = getLanguageNote(
    translationState,
    isShowingOriginalLanguage,
    t,
  );

  return (
    <div className='conversation-review-modal__section conversation-review-modal__section--answer'>
      <div className='conversation-review-modal__section-header'>
        <div className='conversation-review-modal__section-label'>
          <MaterialIcon
            type='outlined'
            icon='smart_toy'
            classes={[
              'conversation-review-modal__section-label-icon',
              isShowingOriginalLanguage
                ? 'conversation-review-modal__section-label-icon--amber'
                : 'conversation-review-modal__section-label-icon--blue',
            ]}
          />
          <span
            className={`conversation-review-modal__section-label-text ${
              isShowingOriginalLanguage
                ? 'conversation-review-modal__section-label-text--amber'
                : 'conversation-review-modal__section-label-text--blue'
            }`}>
            {t('train-ai.conversation-review.answer-label')}
          </span>
        </div>
        {languageNote && (
          <span className='conversation-review-modal__translation-note'>
            {languageNote}
          </span>
        )}
      </div>
      <div className='conversation-review-modal__answer-box'>
        <div className='conversation-review-modal__answer-text'>
          {parse(purify(displayedAnswer))}
        </div>
      </div>
    </div>
  );
};
